import { useCallback, useEffect, useRef, useState } from "react";
import { Send } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { Button } from "../components/Button";
import { useKeyboardOffset } from "../lib/useKeyboardOffset";
import {
  getChatHistory,
  getSuggestions,
  streamChat,
  type Me,
  type Suggestions,
} from "../api";

interface Msg {
  role: "user" | "assistant";
  text: string;
  streaming?: boolean;
}

export function Chat({
  me: _me,
  onSignOut: _onSignOut,
  pendingSeed,
  onSeedConsumed,
  onTurnComplete,
  embedded = false,
}: {
  me: Me;
  /** Kept for backwards-compatibility; sign-out now lives in the top-bar
   * profile menu rendered by AppShell, so the Chat surface doesn't render
   * its own logout control. */
  onSignOut: () => void;
  /** When set (typically by a quick-ask handing off to the Chat tab), fire
   * this as the next user message and then call onSeedConsumed. */
  pendingSeed?: string | null;
  onSeedConsumed?: () => void;
  /** Fires after each assistant turn finishes. The onboarding screen uses it
   * to re-check whether the profile is now complete. */
  onTurnComplete?: () => void;
  /** When true, fill the parent (h-full) instead of the viewport — used by the
   * desktop ChatDock popup, which sizes its own container. */
  embedded?: boolean;
}) {
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [suggestions, setSuggestions] = useState<Suggestions>({ categories: {} });
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const composerRef = useRef<HTMLDivElement>(null);
  useKeyboardOffset(composerRef);

  const refreshSuggestions = useCallback(() => {
    getSuggestions()
      .then(setSuggestions)
      .catch(() => setSuggestions({ categories: {} }));
  }, []);

  useEffect(() => {
    let cancelled = false;
    getChatHistory()
      .then((history) => {
        if (cancelled) return;
        setMessages(history.map((m) => ({ role: m.role, text: m.text })));
      })
      .finally(() => {
        if (!cancelled) setLoadingHistory(false);
      });
    refreshSuggestions();
    return () => {
      cancelled = true;
    };
  }, [refreshSuggestions]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  // Auto-focus the input so you can just start typing. Fires:
  //   - once after history finishes loading (initial mount)
  //   - after every agent turn completes (busy flips back to false)
  useEffect(() => {
    if (loadingHistory) return;
    if (busy) return;
    inputRef.current?.focus();
  }, [loadingHistory, busy]);

  // Consume a seeded message handed off from an in-screen "Ask" button.
  // Only fire once history is loaded so the new turn appends to the right tail.
  useEffect(() => {
    if (!pendingSeed) return;
    if (loadingHistory) return;
    if (busy) return;
    const text = pendingSeed;
    onSeedConsumed?.();
    void send(text);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSeed, loadingHistory, busy]);

  // Tapping a suggestion sends it straight away — pre-filling the input and
  // waiting for Enter trips up people new to chat UIs.
  function pickSuggestion(text: string) {
    void send(text);
  }

  async function send(overrideText?: string) {
    const text = (overrideText ?? input).trim();
    if (!text || busy) return;
    setBusy(true);
    if (!overrideText) setInput("");
    setMessages((m) => [
      ...m,
      { role: "user", text },
      { role: "assistant", text: "", streaming: true },
    ]);

    try {
      for await (const event of streamChat(text)) {
        if (event.type === "delta") {
          setMessages((m) => {
            const last = m[m.length - 1];
            if (!last || last.role !== "assistant") return m;
            return [...m.slice(0, -1), { ...last, text: last.text + event.text }];
          });
        } else if (event.type === "done") {
          setMessages((m) => {
            const last = m[m.length - 1];
            if (!last || last.role !== "assistant") return m;
            return [...m.slice(0, -1), { ...last, streaming: false }];
          });
        } else if (event.type === "error") {
          setMessages((m) => [
            ...m.slice(0, -1),
            { role: "assistant", text: `error: ${event.message}`, streaming: false },
          ]);
        }
      }
    } finally {
      setBusy(false);
      // User state may have changed (group created, RSVP added, etc.) — refresh
      // the contextual chips so they reflect the new state on the next turn.
      refreshSuggestions();
      onTurnComplete?.();
    }
  }

  // The "Ideas:" row prefers conversation-aware chips from the server; when
  // there are none (no history / generation failed) it falls back to flattening
  // the static, state-based categories.
  const quickIdeas =
    suggestions.quick_ideas && suggestions.quick_ideas.length > 0
      ? suggestions.quick_ideas
      : flattenQuickIdeas(suggestions);
  const showQuickIdeas = !busy && !input && messages.length > 0 && quickIdeas.length > 0;

  return (
    // Chat fills the viewport below the AppShell top bar.
    // - 100dvh (dynamic viewport) instead of 100vh handles iOS Safari's
    //   collapsing URL bar. With plain 100vh the composer can sit below the
    //   visible area mid-scroll.
    // - Composer is the last flex child; messages get flex-1, so the
    //   composer naturally pins to the column's bottom (no fixed positioning
    //   that would fight the soft keyboard).
    <div className={"flex flex-col " + (embedded ? "h-full" : "h-[calc(100dvh-64px)]")}>
      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-3xl px-4 py-6">
          {loadingHistory ? (
            <LoadingState />
          ) : messages.length === 0 ? (
            <EmptyState suggestions={suggestions} onPick={pickSuggestion} />
          ) : (
            <div className="space-y-3">
              {messages.map((m, i) => (
                <Bubble key={i} msg={m} />
              ))}
            </div>
          )}
        </div>
      </div>

      <div
        ref={composerRef}
        className="border-t border-ink/10 bg-bg/95 backdrop-blur shrink-0"
        style={{
          paddingBottom: "env(safe-area-inset-bottom)",
          // Shift up by the soft-keyboard height on iOS so the composer
          // stays above the keyboard. --kb-offset is set by useKeyboardOffset.
          transform: "translateY(calc(-1 * var(--kb-offset, 0px)))",
          transition: "transform 0.15s ease-out",
        }}
      >
        <div className="mx-auto max-w-3xl px-4 pt-2 pb-3">
          {showQuickIdeas && <QuickIdeasRow ideas={quickIdeas} onPick={pickSuggestion} />}
          <div className="flex items-end gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Message ClubBot…"
              disabled={busy}
              className="flex-1"
            />
            <Button
              onClick={() => send()}
              disabled={busy || !input.trim()}
              className="aspect-square !px-3 !py-2.5"
              aria-label="Send"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Builds the small inline chip row by taking one from each category in turn,
// then a second from each until we have 5, preserving server order.
function flattenQuickIdeas(s: Suggestions, max = 5): string[] {
  const entries = Object.values(s.categories);
  const out: string[] = [];
  let i = 0;
  while (out.length < max) {
    let advanced = false;
    for (const items of entries) {
      if (i < items.length) {
        out.push(items[i]!);
        advanced = true;
        if (out.length >= max) break;
      }
    }
    if (!advanced) break;
    i++;
  }
  return out;
}

function LoadingState() {
  return (
    <div className="mt-24 flex items-center justify-center gap-2 font-mono text-xs tracking-wider text-paper-dim">
      <span className="typing-dot inline-block h-1.5 w-1.5 rounded-full bg-paper-muted" />
      <span
        className="typing-dot inline-block h-1.5 w-1.5 rounded-full bg-paper-muted"
        style={{ animationDelay: "0.15s" }}
      />
      <span
        className="typing-dot inline-block h-1.5 w-1.5 rounded-full bg-paper-muted"
        style={{ animationDelay: "0.3s" }}
      />
      <span className="ml-2">LOADING HISTORY</span>
    </div>
  );
}

function EmptyState({
  suggestions,
  onPick,
}: {
  suggestions: Suggestions;
  onPick: (text: string) => void;
}) {
  const categories = Object.entries(suggestions.categories);

  return (
    <div className="mx-auto mt-8 max-w-xl">
      <div className="text-center">
        <span className="inline-block mb-3 font-mono text-[10px] uppercase tracking-[0.22em] bg-accent text-ink px-1.5 py-0.5">
          Start the conversation
        </span>
        <h2 className="font-display text-3xl font-bold tracking-tight text-ink">
          Say hi to ClubBot.
        </h2>
        <p className="mt-3 text-[15px] leading-relaxed text-ink/65">
          Schedule recurring play, fan out a pickup beacon, change digest cadence,
          or run a one-off reminder. Try one of these — or type your own.
        </p>
      </div>

      {categories.length > 0 ? (
        <div className="mt-10 space-y-6">
          {categories.map(([category, items]) => (
            <div key={category}>
              <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.22em] text-ink/45">
                {category}
              </p>
              <div className="space-y-1.5">
                {items.map((text) => (
                  <SuggestionRow key={text} text={text} onClick={() => onPick(text)} />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="mt-10 text-center font-mono text-xs text-ink/45">
          Just type a message to get started.
        </p>
      )}
    </div>
  );
}

function SuggestionRow({ text, onClick }: { text: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="group flex w-full items-center justify-between gap-3 rounded-md border-2 border-ink/15 bg-bg px-4 py-2.5 text-left text-[15px] text-ink/80 transition hover:border-ink/40 hover:bg-bg-2"
    >
      <span>{text}</span>
      <span className="font-mono text-xs text-ink/40 transition-colors group-hover:text-ink/70">
        ↵
      </span>
    </button>
  );
}

function QuickIdeasRow({ ideas, onPick }: { ideas: string[]; onPick: (text: string) => void }) {
  return (
    <div className="mb-2 flex items-center gap-1.5">
      <span className="shrink-0 font-mono text-[10px] uppercase tracking-[0.18em] text-ink/45">
        Ideas:
      </span>
      {/* Single scrollable line so the chips never wrap and eat the message
          area on narrow phones — swipe horizontally for the rest. */}
      <div className="no-scrollbar -mx-1 flex gap-1.5 overflow-x-auto px-1">
        {ideas.map((text) => (
          <button
            key={text}
            onClick={() => onPick(text)}
            className="shrink-0 whitespace-nowrap rounded-full border border-ink/15 bg-bg px-3 py-1 text-xs text-ink/70 transition hover:border-ink/40 hover:text-ink"
          >
            {text}
          </button>
        ))}
      </div>
    </div>
  );
}

function Bubble({ msg }: { msg: Msg }) {
  const isUser = msg.role === "user";
  if (isUser) {
    return (
      <div className="flex justify-end">
        <div className="max-w-[82%] rounded-2xl rounded-br-sm bg-you px-4 py-2.5 text-[15px] leading-relaxed text-bg whitespace-pre-wrap">
          {msg.text}
        </div>
      </div>
    );
  }
  return (
    <div className="flex justify-start">
      <div className="chat-md max-w-[82%] rounded-2xl rounded-bl-sm border border-ink/10 bg-bg-2 px-4 py-3 text-[15px] leading-relaxed text-ink">
        {msg.text ? (
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.text}</ReactMarkdown>
        ) : msg.streaming ? (
          <span className="inline-flex items-center gap-1">
            <span className="typing-dot inline-block h-1.5 w-1.5 rounded-full bg-ink/40" />
            <span
              className="typing-dot inline-block h-1.5 w-1.5 rounded-full bg-ink/40"
              style={{ animationDelay: "0.15s" }}
            />
            <span
              className="typing-dot inline-block h-1.5 w-1.5 rounded-full bg-ink/40"
              style={{ animationDelay: "0.3s" }}
            />
          </span>
        ) : null}
      </div>
    </div>
  );
}
